import React, { useContext, useState, useEffect } from 'react';
import { injectIntl } from 'react-intl';
import Paragraph from '../../../../atoms/paragraphs/Paragraph';
import Span from '../../../../atoms/spans/Span';
import Button from '../../../../atoms/buttons/Button';
import { AppContext } from '../../../../../contexts/AppContext';
import Menu, { MENU_CATEGORY } from '../../../../organisms/menu/Menu';
import { ContactService } from '../../../../../services';
import { BillingDataHelper } from './helper';
import Input from '../../../../atoms/inputs/Input';
import Icon from '../../../../atoms/icons/Icon';
import HandleCallResultModal from '../../../feedback/modal/HandleCallResultModal';
import DOMPurify from 'dompurify';
import parse from 'html-react-parser';
import HistoryComponent from '../../../../organisms/history/HistoryComponent';
import InputVATValidator from '../../../../organisms/inputs/InputVATValidator';
import Skeleton from 'react-loading-skeleton';

const initialFields = {
  billing_country_id: '',
  billing_fiscal_id: '',
  billing_organization_name: '',
  billing_address: '',
  billing_city: '',
  billing_postal_code: '',
  billing_email: '',
};

const BillingDataLayout = ({ intl, ...props }) => {
  const { appState } = useContext(AppContext);
  const [fields, setFields] = useState(initialFields);
  const [accountSettings, setAccountSettings] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [callSucceeded, setCallSucceeded] = useState(false);
  const [callResponse, setCallResponse] = useState(null);
  const [postalCodeError, setPostalCodeError] = useState(false);
  const [nifValidated, setNifValidated] = useState(false);
  const [billingHistory, setBillingHistory] = useState({
    billing_history: [],
    pagination: {
      total_pages: 1,
    },
  });
  const [historyPage, setHistoryPage] = useState(1);

  const historyConfig = {
    sequence_number: {
      headerClassName: 'col-5 col-md-2',
      headerValue: intl.messages['billingHistoryHeader_DocumentNumber'],
      rowClassName: 'col-5 col-md-2 font-bold color-gray-500',
    },
    date: {
      headerClassName: 'col-5 col-md-2',
      headerValue: intl.messages['billingHistoryHeader_Date'],
      rowClassName: 'col-5 col-md-2 color-gray-500',
      formatValue: (date) =>
        new Intl.DateTimeFormat('pt-PT', {
          timeZone: 'Europe/Lisbon',
        }).format(new Date(date)),
    },
    total_before_taxes: {
      headerClassName: 'd-none d-md-flex col-5 col-md-2',
      headerValue: intl.messages['billingHistoryHeader_RawAmount'],
      rowClassName: 'col-5 col-md-2 color-gray-500 d-none d-md-flex',
      suffix: ' €',
      collapse: true,
    },
    total_taxes: {
      headerClassName: 'd-none d-md-flex col-5 col-md-2',
      headerValue: intl.messages['billingHistoryHeader_TaxAmount'],
      rowClassName: 'col-5 col-md-2 color-gray-500 d-none d-md-flex',
      suffix: ' €',
    },
    total: {
      headerClassName: 'col-5 col-md-2',
      headerValue: intl.messages['billingHistoryHeader_TotalAmount'],
      rowClassName: 'col-5 col-md-2 color-gray-500',
      suffix: ' €',
    },
  };

  const historyActions = {
    clickable: true,
    onAction: (element) => {
      window.open(element.permalink, '_blank');
    },
  };

  useEffect(() => {
    (async () => {
      const { account } = await ContactService.getAccountSettings();
      const billingHistory =
        await ContactService.getBillingHistory(historyPage);
      setAccountSettings(account);
      setBillingHistory(billingHistory);
      const keys = Object.keys(initialFields);
      setFields((prev) => ({
        ...prev,
        ...keys.reduce(
          (acc, key) => ({ ...acc, [key]: account[key] || '' }),
          {}
        ),
      }));
    })();
    // eslint-disable-next-line
  }, [appState.accountId]);

  const onChangeField = (key, value) => setFields({ ...fields, [key]: value });

  const onPageChange = async (page) => {
    setHistoryPage(page);
    const billingHistory = await ContactService.getBillingHistory(page);
    setBillingHistory(billingHistory);
  };

  const isButtonDisabled = () => {
    if (nifValidated) return false;
    const {
      billing_organization_name,
      billing_address,
      billing_city,
      billing_fiscal_id,
      billing_country_id,
      billing_email,
    } = fields;
    const {
      billing_fiscal_id: accountFiscalId,
      billing_country_id: accountTaxLocation,
    } = accountSettings;
    return (
      submitting ||
      !billing_organization_name ||
      !billing_address ||
      !billing_city ||
      !billing_fiscal_id ||
      !billing_email ||
      billing_fiscal_id !== accountFiscalId ||
      billing_country_id !== accountTaxLocation
    );
  };

  const responseErrorParser = (response) => {
    if (response.error) {
      const parsed = parse(DOMPurify.sanitize(response.error));
      return parsed;
    } else if (response.errors) {
      const parsed = parse(DOMPurify.sanitize(response.errors[0]['error']));
      return parsed;
    } else {
      return false;
    }
  };
  const onSubmit = async () => {
    setSubmitting(true);
    setIsSubmitted(true);
    setCallResponse('taxDataProcessing');

    const changedFields = Object.keys(fields).reduce((acc, key) => {
      const value =
        typeof fields[key] === 'string' ? fields[key].trim() : fields[key];
      if (value !== accountSettings[key]) acc[key] = value;
      return acc;
    }, {});

    if (!Object.keys(changedFields).length) {
      setSubmitting(false);
      setCallResponse('noChanges');
      setCallSucceeded(true);
      return;
    }

    const response = await ContactService.updateAccountSettings(changedFields);
    setSubmitting(false);

    if (!responseErrorParser(response)) {
      setAccountSettings(response.account);
      setCallResponse('taxDataSuccess');
      setCallSucceeded(true);
    } else {
      const parsed = responseErrorParser(response);
      setCallResponse(parsed);
      setCallSucceeded(false);
    }
  };

  const validationResponse = (country, vatNumber, response, result) => {
    if (response.address) {
      const parsedAddress = BillingDataHelper.parseAddressObject(
        response.address
      );

      setFields({
        ...fields,
        billing_fiscal_id: vatNumber,
        billing_country_id: country.value,
        billing_organization_name: response.name,
        billing_address: parsedAddress.street,
        billing_city: parsedAddress.city,
        billing_postal_code: parsedAddress.postal_code,
      });
    } else {
      setFields({
        ...fields,
        billing_fiscal_id: vatNumber,
        billing_country_id: country.value,
      });
    }

    setNifValidated(result);
  };

  const handlePostalCodeChange = (e) => {
    const value = e.target.value;
    onChangeField('billing_postal_code', value);
    setPostalCodeError(!/^\d{4}-\d{3}$/.test(value));
  };

  return (
    <div id='main-content' className='container --billing-data'>
      <div className='row mx-0 justify-content-center'>
        <div className='col-12 col-lg-9 ps-md-0 '>
          <div className='d-flex flex-column w-100'>
            <div className='col-12 px-0'>
              <Paragraph className='text-header h2'>
                {intl.messages['billingDataTitle']}
              </Paragraph>
              <Paragraph className='text-paragraph mt-3 mb-2'>
                <Span className='d-block'>
                  {intl.messages['billingDataDescription']}
                </Span>
              </Paragraph>
              <div className='sep-light mt-2'></div>
            </div>
            <div className='form-container d-flex flex-column w-100 mt-4'>
              <div className='col-12 col-md-6 selector-container mt-4'>
                <div className='selector-wrapper'>
                  <label className='text-label mb-3'>
                    {intl.messages['taxDataFiscalId']}
                  </label>
                  <div className='d-flex flex-row w-100'>
                    {(fields.billing_country_id && (
                      <InputVATValidator
                        defaultVatNumber={fields.billing_fiscal_id}
                        defaultCountry={{
                          country_id: fields.billing_country_id,
                        }}
                        validationResponse={validationResponse}
                      />
                    )) || <Skeleton height={40} containerClassName='w-100' />}
                  </div>
                </div>
              </div>
              <div className='form-container d-flex flex-wrap w-100 mt-4'>
                <div className='col-12 col-md-6 selector-container mt-4'>
                  <div className='selector-wrapper'>
                    <label
                      className='text-label mb-3'
                      htmlFor='organization_name'
                    >
                      {intl.messages['companyName']}
                    </label>
                    <Input
                      data-testid='company-name-field'
                      id='organization_name'
                      type='text'
                      value={fields.billing_organization_name}
                      onChange={(e) =>
                        onChangeField(
                          'billing_organization_name',
                          e.target.value
                        )
                      }
                      disabled={submitting}
                    />
                  </div>
                </div>
                <div className='col-12 col-md-6 selector-container mt-4'>
                  <div className='selector-wrapper'>
                    <label className='text-label mb-3' htmlFor='billing_email'>
                      {intl.messages['billingDataEmail']}
                    </label>
                    <Input
                      data-testid='email-field'
                      id='billing_email'
                      type='text'
                      value={fields.billing_email}
                      onChange={(e) =>
                        onChangeField('billing_email', e.target.value)
                      }
                      disabled={submitting}
                    />
                  </div>
                </div>
                <div className='col-12 col-md-6 selector-container mt-4'>
                  <div className='selector-wrapper'>
                    <label className='text-label mb-3' htmlFor='address'>
                      {intl.messages['address']}
                    </label>
                    <Input
                      data-testid='address-field'
                      id='address'
                      type='text'
                      value={fields.billing_address}
                      onChange={(e) =>
                        onChangeField('billing_address', e.target.value)
                      }
                      disabled={submitting}
                    />
                  </div>
                </div>
                <div className='col-12 col-md-6 selector-container mt-4'>
                  <div className='selector-wrapper'>
                    <label className='text-label mb-3' htmlFor='city'>
                      {intl.messages['city']}
                    </label>
                    <Input
                      data-testid='city-field'
                      id='city'
                      type='text'
                      value={fields.billing_city}
                      onChange={(e) =>
                        onChangeField('billing_city', e.target.value)
                      }
                      disabled={submitting}
                    />
                  </div>
                </div>
                <div className='col-12 col-md-6 selector-container mt-4'>
                  <div className='selector-wrapper'>
                    <label className='text-label mb-3' htmlFor='postal_code'>
                      {intl.messages['postalCode']}
                    </label>
                    <Input
                      data-testid='postal-code-field'
                      id='postal_code'
                      type='text'
                      value={fields.billing_postal_code}
                      onChange={handlePostalCodeChange}
                      disabled={submitting}
                    />
                    {postalCodeError && (
                      <div
                        data-testid='postal-code-error'
                        className='input-error'
                        id='postal_code_error'
                      >
                        <Icon className='icon fas fa-circle-exclamation' />
                        {intl.messages['invalidPostalCodeFormat']}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className='d-flex col-12 px-0 mt-4'>
                <div className='d-flex w-100 flex-column flex-sm-row justify-content-end'>
                  <Button
                    data-testid='save-button'
                    className='button button-primary ms-auto ms-sm-0'
                    id='save_button'
                    label={intl.messages['save']}
                    onClick={onSubmit}
                    disabled={isButtonDisabled() || postalCodeError}
                  />
                </div>
              </div>
            </div>
            <div className='sep-light mt-2'></div>
            <div className='mt-4'>
              <HistoryComponent
                config={historyConfig}
                data={billingHistory.billing_history}
                pagination={billingHistory.pagination}
                actions={historyActions}
                historyPage={historyPage}
                historyTitle={intl.messages['billingHistoryTitle']}
                onPageChange={onPageChange}
              ></HistoryComponent>
            </div>
          </div>
        </div>
        <div className='col-lg-3 pe-md-0'>
          <Menu {...props} activeCategory={MENU_CATEGORY.ACCOUNT} />
        </div>
      </div>
      {isSubmitted && (
        <HandleCallResultModal
          data-testid='handle-call-result-modal'
          isLoading={submitting}
          succeeded={callSucceeded}
          message={callResponse}
          onClose={() => {
            setIsSubmitted(false);
            setCallResponse(null);
          }}
          showCloseButton
        />
      )}
    </div>
  );
};

export default injectIntl(BillingDataLayout);
